import React, { useState, useEffect } from 'react';
import {
  Box, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  Text, Badge, Button, IconButton, HStack, VStack, Flex,
  useColorModeValue, Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalFooter, ModalBody, ModalCloseButton, FormControl, FormLabel,
  Input, Select, useDisclosure, Switch, useToast, InputGroup,
  InputLeftElement, Card, CardBody, Stat, StatLabel, StatNumber,
  SimpleGrid, Menu, MenuButton, MenuList, MenuItem, Spinner, Center,
  Alert, AlertIcon, AlertTitle, AlertDescription, Tooltip,
  InputLeftAddon,
} from '@chakra-ui/react';
import {
  MdSearch, MdRefresh, MdAdd, MdMoreVert, MdEdit, MdDelete,
  MdCheckCircle, MdCancel, MdOpenInNew, MdWarning,
} from 'react-icons/md';
import apiService from '../services/apiService';
import { FiRefreshCw } from 'react-icons/fi';
import usageTracker from '../services/usageTracker';

const UrlManager = ({ userProfile, appConfig, authServiceReady }) => {
  const bgColor            = useColorModeValue('gray.50',      'gray.900');
  const cardBg             = useColorModeValue('white',        'gray.800');
  const borderColor        = useColorModeValue('gray.200',     'gray.700');
  const textColor          = useColorModeValue('gray.600',     'gray.300');
  const headingColor       = useColorModeValue('gray.800',     'white');
  const mutedTextColor     = useColorModeValue('gray.500',     'gray.400');
  const inputBg            = useColorModeValue('white',        'gray.700');
  const hoverBg            = useColorModeValue('gray.50',      'gray.700');
  const tableHeaderBg      = useColorModeValue('gray.50',      'gray.900');
  const statsCardBg        = useColorModeValue('white',        'gray.800');
  const btnHoverBorder     = useColorModeValue('gray.300',     'gray.600');
  const purpleHoverBg      = useColorModeValue('purple.50',    'purple.900');
  const purpleBorder       = useColorModeValue('purple.200',   'purple.600');
  const purpleText         = useColorModeValue('purple.600',   'purple.300');
  const purpleHoverBorder  = useColorModeValue('purple.300',   'purple.500');
  const blueHover          = useColorModeValue('blue.600',     'blue.500');
  const badgeVariant       = useColorModeValue('subtle',       'solid');
  const modalOverlay       = useColorModeValue('blackAlpha.300','blackAlpha.700');
  const menuShadow         = useColorModeValue('lg',           'dark-lg');
  const redHoverBg         = useColorModeValue('red.50',       'red.900');
  const searchIconColor    = useColorModeValue('gray.400',     'gray.500');
  const addonBg            = useColorModeValue('gray.100',     'gray.600');
  const addonColor         = useColorModeValue('gray.600',     'gray.300');

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const [urlMonitors,    setUrlMonitors]    = useState([]);
  const [clients,        setClients]        = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [searchTerm,     setSearchTerm]     = useState('');
  const [statusFilter,   setStatusFilter]   = useState('All Status');
  const [clientFilter,   setClientFilter]   = useState('');           // ← '' = All Clients
  const [pageSize,       setPageSize]       = useState('20 per page');
  const [currentPage,    setCurrentPage]    = useState(1);
  const [isEditMode,     setIsEditMode]     = useState(false);
  const [editingUrl,     setEditingUrl]     = useState(null);

  const emptyForm = { endpoint: '',  status: 1, clientId: '' };
  const [newUrl,        setNewUrl]        = useState(emptyForm);
  const [endpointError, setEndpointError] = useState('');

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [urlToDelete,     setUrlToDelete]     = useState(null);

  // ── Derived: selected client & baseUrl ────────────────────────────────────
  const selectedClient  = clients.find(c => String(c.clientId) === String(newUrl.clientId)) || null;
  const baseUrl         = selectedClient?.baseUrl || '';
  const baseUrlMissing  = newUrl.clientId && !baseUrl;

  // ─── Fetch clients ─────────────────────────────────────────────────────────
  const fetchClients = async () => {
    try {
      setClientsLoading(true);
      const res = await apiService.getClientsList({ size: 1000 });
      if (res.success && res.data) setClients(res.data.content);
      else throw new Error(res.error || 'Failed to fetch clients');
    } catch (err) {
      console.error(err);
      toast({ title: 'Warning', description: 'Could not load client list.', status: 'warning', duration: 4000, isClosable: true });
    } finally { setClientsLoading(false); }
  };

  // ─── Fetch URL monitors (optional clientId filter → API param) ────────────
  const fetchUrlMonitors = async (clientId = '') => {
    try {
      setLoading(true); setError(null);
      const res = clientId
        ? await apiService.fetchUrlMonitorsByClient(clientId)   // e.g. GET /monitors?clientId=15
        : await apiService.fetchUrlMonitors();
      if (res.success && res.data) setUrlMonitors(Array.isArray(res.data) ? res.data : []);
      else throw new Error(res.error || res.message || 'Failed to fetch');
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load URL monitors.');
      setUrlMonitors([]);
    } finally { setLoading(false); }
  };

  // Re-fetch whenever clientFilter changes
  useEffect(() => { fetchUrlMonitors(clientFilter); }, [clientFilter]);

  useEffect(() => { fetchClients(); }, []);

  // ─── Stats ─────────────────────────────────────────────────────────────────
  const stats = {
    total:    urlMonitors.length,
    active:   urlMonitors.filter(u => u.status === 1).length,
    inactive: urlMonitors.filter(u => u.status === 0).length,
  };

  // ─── Helpers ───────────────────────────────────────────────────────────────
  const handleSearch  = val => { setSearchTerm(val); setCurrentPage(1); };
  const resetFilters  = () => { setStatusFilter('All Status'); setClientFilter(''); setSearchTerm(''); setCurrentPage(1); };
  const handleRefresh = () => {
    fetchUrlMonitors(clientFilter); fetchClients();
    toast({ title: 'Refreshed', status: 'info', duration: 2000, isClosable: true });
  };

  const handleCreateNew  = () => { setIsEditMode(false); setEditingUrl(null); setNewUrl(emptyForm); setEndpointError(''); onOpen(); };
  const handleModalClose = () => { setIsEditMode(false); setEditingUrl(null); setNewUrl(emptyForm); setEndpointError(''); onClose(); };

  const validateEndpoint = ep => {
    if (!ep) return 'Endpoint is required.';
    if (!ep.startsWith('/')) return 'Endpoint must start with / (e.g. /health)';
    return '';
  };

  // ─── Save (create / update) ────────────────────────────────────────────────
  const handleSave = async () => {
    if (!newUrl.clientId) {
      toast({ title: 'Error', description: 'Please select a client.', status: 'error', duration: 3000, isClosable: true });
      return;
    }
    if (baseUrlMissing) {
      toast({ title: 'Client Base URL Missing', description: 'Please update the base URL for this client first.', status: 'error', duration: 4000, isClosable: true });
      return;
    }
    const epErr = validateEndpoint(newUrl.endpoint);
    if (epErr) { setEndpointError(epErr); return; }
    

    const payload = { url: newUrl.endpoint,  status: newUrl.status, clientId: newUrl.clientId };

    try {
      if (isEditMode) {
        payload.id = editingUrl.id;
        const res = await apiService.updateUrlMonitor(payload);
        if (res.success && res.data) {
          usageTracker.action('URL Monitor', 'UPDATE');
          handleRefresh();
          handleModalClose();
          toast({ title: 'URL Monitor Updated', description: `${res.data.fullurl || res.data.url} updated.`, status: 'success', duration: 3000, isClosable: true });
        } else throw new Error(res.error || res.message || 'Failed to update');
      } else {
        const res = await apiService.createUrlMonitor(payload);
        if (res.success && res.data) {
          usageTracker.action('URL Monitor', 'CREATE');
          handleRefresh();
          handleModalClose();
          toast({ title: 'URL Monitor Created', description: `${res.data.fullurl || res.data.url} created.`, status: 'success', duration: 3000, isClosable: true });
        } else throw new Error(res.error || res.message || 'Failed to create');
      }
    } catch (err) {
      console.error(err);
      toast({ title: 'Error', description: err.message || 'Operation failed.', status: 'error', duration: 3000, isClosable: true });
    }
  };

  // ── On edit: `urlMonitor.url` is already just the endpoint (e.g. /health) ─
  const handleEdit = urlMonitor => {
    setIsEditMode(true);
    setEditingUrl(urlMonitor);
    setNewUrl({
      endpoint:    urlMonitor.url,           // ← already just the path
      status:      urlMonitor.status,
      clientId:    String(urlMonitor.clientId) || '',
    });
    setEndpointError('');
    onOpen();
  };

  // ─── Delete ────────────────────────────────────────────────────────────────
  const handleDelete  = u => { setUrlToDelete(u); setDeleteModalOpen(true); };
  const cancelDelete  = () => { setDeleteModalOpen(false); setUrlToDelete(null); };
  const confirmDelete = async () => {
    if (!urlToDelete) return;
    try {
      const res = await apiService.deleteUrlMonitor(urlToDelete.id);
      if (res.success) {
          usageTracker.action('URL Monitor', 'DELETE');
        handleRefresh();
        toast({ title: 'Deleted', description: `${urlToDelete.fullurl} deleted.`, status: 'success', duration: 3000, isClosable: true });
        cancelDelete();
      } else throw new Error('Failed to delete');
    } catch {
      toast({ title: 'Error', description: 'Failed to delete URL monitor.', status: 'error', duration: 3000, isClosable: true });
    }
  };

  // ─── Filter & Paginate ─────────────────────────────────────────────────────
  const filteredUrls = urlMonitors.filter(u => {
    const matchesSearch = !searchTerm ||
      (u.fullurl  || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (u.clientName || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'All Status' ||
      (statusFilter === 'active'   && u.status === 1) ||
      (statusFilter === 'inactive' && u.status === 0);
    return matchesSearch && matchesStatus;
  });

  const itemsPerPage  = parseInt(pageSize.split(' ')[0]);
  const totalPages    = Math.ceil(filteredUrls.length / itemsPerPage);
  const startIndex    = (currentPage - 1) * itemsPerPage;
  const paginatedUrls = filteredUrls.slice(startIndex, startIndex + itemsPerPage);

  const getStatusBadge     = s => s === 1
    ? { colorScheme: 'green', label: 'UP',   icon: MdCheckCircle }
    : { colorScheme: 'red',   label: 'DOWN', icon: MdCancel };

  // ── Date helpers — uses `lastChecked` / `createdAt` from new response ──────
  const formatDateTime = d => {
    if (!d) return 'N/A';
    const date = new Date(d.replace(' ', 'T').split('.')[0] + 'Z');
    return isNaN(date) ? 'Invalid Date' : date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
  };
  const getTimeAgo = d => {
    if (!d) return 'Never';
    const past = new Date(d.replace(' ', 'T').split('.')[0] + 'Z');
    if (isNaN(past)) return 'Invalid Date';
    const mins = Math.floor((Date.now() - past) / 60000);
    if (mins < 1)   return 'Just now';
    if (mins < 60)  return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)   return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days === 1) return 'Yesterday';
    if (days < 7)   return `${days}d ago`;
    const wks = Math.floor(days / 7);
    if (wks < 4)    return `${wks}w ago`;
    const mos = Math.floor(days / 30);
    if (mos < 12)   return `${mos}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
  };

  // ─── Loading / Error screens ───────────────────────────────────────────────
  if (loading) return (
    <Box bg={bgColor} minH="100vh" p={8}>
      <Center py={20}><VStack spacing={4}><Spinner size="xl" color="blue.500" thickness="4px" /><Text color={textColor} fontSize="lg">Loading URL monitors...</Text></VStack></Center>
    </Box>
  );
  if (error) return (
    <Box bg={bgColor} minH="100vh" p={8}>
      <Alert status="error" bg={cardBg} borderColor="red.300" borderWidth="1px" borderRadius="md">
        <AlertIcon /><Box flex="1"><AlertTitle color="red.600">Error!</AlertTitle><AlertDescription color={textColor}>{error}</AlertDescription></Box>
        <Button size="sm" colorScheme="red" variant="outline" onClick={handleRefresh}>Retry</Button>
      </Alert>
    </Box>
  );

  return (
    <Box bg={bgColor} minH="100vh" p={8}>

      {/* Header */}
      <Flex justify="space-between" align="flex-start" mb={6}>
        <Box>
          <Text fontSize="2xl" fontWeight="bold" color={headingColor} mb={1}>URL Manager</Text>
          <Text color={textColor} fontSize="sm">Manage the URLs that need monitoring</Text>
        </Box>
        <HStack spacing={3}>
          <Button leftIcon={<MdRefresh />} variant="outline" size="sm" borderColor={borderColor} color={textColor} onClick={handleRefresh} _hover={{ bg: hoverBg, borderColor: btnHoverBorder }}>Refresh</Button>
          <Button leftIcon={<MdAdd />} colorScheme="blue" size="sm" onClick={handleCreateNew} _hover={{ bg: blueHover }}>Add URL Monitor</Button>
        </HStack>
      </Flex>

      {/* Stats */}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={6}>
        <Card bg={statsCardBg} boxShadow="sm" borderWidth="1px" borderColor={borderColor}><CardBody><Stat><StatLabel color={textColor} fontSize="sm">Total URLs</StatLabel><StatNumber fontSize="2xl" fontWeight="bold" color={headingColor}>{stats.total}</StatNumber></Stat></CardBody></Card>
        <Card bg={statsCardBg} boxShadow="sm" borderWidth="1px" borderColor={borderColor}><CardBody><Stat><StatLabel color={textColor} fontSize="sm">Active (UP)</StatLabel><StatNumber fontSize="2xl" fontWeight="bold" color="green.500">{stats.active}</StatNumber></Stat></CardBody></Card>
        <Card bg={statsCardBg} boxShadow="sm" borderWidth="1px" borderColor={borderColor}><CardBody><Stat><StatLabel color={textColor} fontSize="sm">Inactive (DOWN)</StatLabel><StatNumber fontSize="2xl" fontWeight="bold" color="red.500">{stats.inactive}</StatNumber></Stat></CardBody></Card>
      </SimpleGrid>

      {/* Filters */}
      <Card bg={cardBg} boxShadow="sm" mb={6} borderWidth="1px" borderColor={borderColor}>
        <CardBody>
          <Flex gap={4} mb={4} align="end">
            <Box flex="1">
              <InputGroup>
                <InputLeftElement pointerEvents="none"><MdSearch color={searchIconColor} /></InputLeftElement>
                <Input placeholder="Search by full URL or client name" value={searchTerm} onChange={e => handleSearch(e.target.value)} bg={inputBg} borderColor={borderColor} color={headingColor} _placeholder={{ color: mutedTextColor }} />
              </InputGroup>
            </Box>
            <Button variant="outline" colorScheme="purple" size="md" onClick={resetFilters} borderColor={purpleBorder} color={purpleText} _hover={{ bg: purpleHoverBg, borderColor: purpleHoverBorder }}>Reset Filters</Button>
          </Flex>
          <Text fontSize="sm" fontWeight="medium" color={headingColor} mb={3}>Filters</Text>
          <HStack spacing={4} mb={4}>
            <FormControl maxW="300px">
              <FormLabel fontSize="sm" color={textColor}>Client</FormLabel>
              <Select
                size="sm"
                value={clientFilter}
                onChange={e => { setClientFilter(e.target.value); setCurrentPage(1); }}
                bg={inputBg} borderColor={borderColor} color={headingColor}
                isDisabled={clientsLoading}
              >
                <option value="">All Clients</option>
                {clients.map(c => <option key={c.clientId} value={c.clientId}>{c.clientName}</option>)}
              </Select>
            </FormControl>
            <FormControl maxW="300px">
            <FormLabel fontSize="sm" color={textColor}>Status</FormLabel>
              <Select size="sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} bg={inputBg} borderColor={borderColor} color={headingColor}>
                <option value="All Status">All Status</option>
                <option value="active">Active (UP)</option>
                <option value="inactive">Inactive (DOWN)</option>
              </Select>
            </FormControl>
            <FormControl maxW="200px">
              <FormLabel fontSize="sm" color={textColor}>Page Size</FormLabel>
              <Select size="sm" value={pageSize} onChange={e => { setPageSize(e.target.value); setCurrentPage(1); }} bg={inputBg} borderColor={borderColor} color={headingColor}>
                <option value="20 per page">20 per page</option>
                <option value="50 per page">50 per page</option>
                <option value="100 per page">100 per page</option>
              </Select>
            </FormControl>
          </HStack>
        </CardBody>
      </Card>

      {/* ── Table ── */}
      <Card bg={cardBg} boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
        <CardBody p={0}>
          <Flex justify="space-between" align="center" p={4} borderBottom="1px" borderBottomColor={borderColor}>
            <Text fontWeight="semibold" fontSize="md" color={headingColor}>URL Monitors ({filteredUrls.length})</Text>
            <HStack spacing={4} fontSize="sm" color={textColor}>
              <Text>PAGE {currentPage} OF {totalPages || 1}</Text>
              <Text>SHOWING {Math.min(itemsPerPage, filteredUrls.length - startIndex)} OF {filteredUrls.length}</Text>
            </HStack>
          </Flex>

          {filteredUrls.length === 0 ? (
            <Center py={10}><VStack spacing={3}>
              <Text color={mutedTextColor} fontSize="lg">No URL monitors found</Text>
              <Text color={mutedTextColor} fontSize="sm">{searchTerm || statusFilter !== 'All Status' ? 'Try adjusting your filters' : 'Add your first URL monitor to get started'}</Text>
            </VStack></Center>
          ) : (
            <>
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead bg={tableHeaderBg} borderBottom="2px" borderColor={borderColor}>
                    <Tr>
                      {['Client', 'Full URL', 'Status', 'Last Checked', 'Actions'].map(h => (
                        <Th key={h} color={headingColor} fontWeight="semibold" fontSize="xs" textTransform="uppercase" letterSpacing="wider" py={4}>{h}</Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {paginatedUrls.map((u, idx) => {
                      const sb         = getStatusBadge(u.status);
                      const StatusIcon = sb.icon;
                      return (
                        <Tr key={`${u.id}-${idx}`} _hover={{ bg: hoverBg }}>

                          {/* Client */}
                          <Td py={4} borderColor={borderColor}>
                            <VStack align="start" spacing={0}>
                              <Text fontSize="sm" fontWeight="medium" color={headingColor}>{u.clientName || '—'}</Text>
                              <Text fontSize="xs" color={mutedTextColor}>{u.baseurl}</Text>
                            </VStack>
                          </Td>

                          {/* Full URL */}
                          <Td py={4} borderColor={borderColor}>
                            <VStack align="start" spacing={1}>
                              <HStack spacing={2}>
                                <Tooltip label="Open in new tab" placement="top">
                                  <Text fontWeight="medium" fontSize="sm" color="blue.500" cursor="pointer" _hover={{ textDecoration: 'underline' }} onClick={() => window.open(u.fullurl, '_blank')} maxW="380px" isTruncated>
                                    {u.fullurl}
                                  </Text>
                                </Tooltip>
                                <IconButton icon={<MdOpenInNew />} size="xs" variant="ghost" color="blue.500" onClick={() => window.open(u.fullurl, '_blank')} aria-label="Open URL" />
                              </HStack>
                              <Text fontSize="xs" color={mutedTextColor}>ID: {u.id}</Text>
                            </VStack>
                          </Td>

                          {/* Status */}
                          <Td py={4} borderColor={borderColor}>
                            <HStack spacing={2}>
                              <StatusIcon size={16} />
                              <Badge colorScheme={sb.colorScheme} variant={badgeVariant} fontSize="xs" px={2} py={1}>{sb.label}</Badge>
                            </HStack>
                          </Td>

                          {/* Last Checked — uses `lastChecked` from response */}
                          <Td py={4} borderColor={borderColor}>
                            <VStack align="start" spacing={0}>
                              <Text fontSize="sm" color={headingColor} fontWeight="medium">{getTimeAgo(u.lastChecked)}</Text>
                              <Text fontSize="xs" color={mutedTextColor}>{formatDateTime(u.lastChecked)}</Text>
                            </VStack>
                          </Td>

                          {/* Actions */}
                          <Td py={4} borderColor={borderColor}>
                            <Menu>
                              <MenuButton as={IconButton} icon={<MdMoreVert />} variant="ghost" size="sm" color={textColor} _hover={{ bg: hoverBg, color: headingColor }} />
                              <MenuList bg={cardBg} borderColor={borderColor} boxShadow={menuShadow}>
                                <MenuItem icon={<MdEdit />}   onClick={() => handleEdit(u)}   bg={cardBg} color={headingColor}  _hover={{ bg: hoverBg }}>Edit</MenuItem>
                                <MenuItem icon={<MdDelete />} onClick={() => handleDelete(u)} bg={cardBg} color="red.500"       _hover={{ bg: redHoverBg }}>Delete</MenuItem>
                              </MenuList>
                            </Menu>
                          </Td>

                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>

              {totalPages > 1 && (
                <Flex justify="space-between" align="center" p={4} borderTop="1px" borderTopColor={borderColor}>
                  <Text fontSize="sm" color={textColor}>Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredUrls.length)} of {filteredUrls.length} entries</Text>
                  <HStack spacing={2}>
                    {['First', 'Previous'].map(lbl => (
                      <Button key={lbl} size="sm" onClick={() => setCurrentPage(lbl === 'First' ? 1 : currentPage - 1)} isDisabled={currentPage === 1} variant="outline" borderColor={borderColor} color={textColor} _hover={{ bg: hoverBg }}>{lbl}</Button>
                    ))}
                    {(() => {
                      const pages = [], maxV = 5;
                      let s = Math.max(1, currentPage - 2), e = Math.min(totalPages, s + maxV - 1);
                      if (e - s < maxV - 1) s = Math.max(1, e - maxV + 1);
                      for (let i = s; i <= e; i++) pages.push(
                        <Button key={i} size="sm" variant={i === currentPage ? 'solid' : 'outline'} colorScheme={i === currentPage ? 'blue' : 'gray'} onClick={() => setCurrentPage(i)} borderColor={i === currentPage ? 'blue.500' : borderColor} color={i === currentPage ? 'white' : textColor} bg={i === currentPage ? 'blue.500' : 'transparent'} _hover={{ bg: i === currentPage ? 'blue.600' : hoverBg }}>{i}</Button>
                      );
                      return pages;
                    })()}
                    {['Next', 'Last'].map(lbl => (
                      <Button key={lbl} size="sm" onClick={() => setCurrentPage(lbl === 'Last' ? totalPages : currentPage + 1)} isDisabled={currentPage === totalPages} variant="outline" borderColor={borderColor} color={textColor} _hover={{ bg: hoverBg }}>{lbl}</Button>
                    ))}
                  </HStack>
                </Flex>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {/* ── Add / Edit Modal ── */}
      <Modal isOpen={isOpen} onClose={handleModalClose} size="lg">
        <ModalOverlay bg={modalOverlay} />
        <ModalContent bg={cardBg} borderColor={borderColor} borderWidth="1px">
          <ModalHeader color={headingColor} borderBottom="1px" borderBottomColor={borderColor}>
            {isEditMode ? 'Edit URL Monitor' : 'Add New URL Monitor'}
          </ModalHeader>
          <ModalCloseButton color={textColor} />
          <ModalBody pb={6}>
            <VStack spacing={4}>

              {/* Client */}
              <FormControl isRequired>
                <FormLabel color={headingColor} fontSize="sm" fontWeight="medium">Client</FormLabel>
                <Select
                  placeholder={clientsLoading ? 'Loading clients...' : 'Select client'}
                  value={newUrl.clientId}
                  onChange={e => { setNewUrl({ ...newUrl, clientId: e.target.value, endpoint: '' }); setEndpointError(''); }}
                  isDisabled={clientsLoading}
                  bg={inputBg} borderColor={borderColor} color={headingColor}
                  _hover={{ borderColor: btnHoverBorder }}
                  _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)' }}
                >
                  {clients.map(c => <option key={c.clientId} value={c.clientId}>{c.clientName}</option>)}
                </Select>
              </FormControl>

              {/* Base URL (read-only / error) */}
              {newUrl.clientId && (
                <FormControl>
                  <FormLabel color={headingColor} fontSize="sm" fontWeight="medium">Client Base URL</FormLabel>
                  {baseUrlMissing ? (
                    <Alert status="error" borderRadius="md" fontSize="sm" py={2} px={3}>
                      <AlertIcon as={MdWarning} boxSize={4} />
                      <Box>
                        <AlertTitle fontSize="xs" fontWeight="bold">Base URL not configured</AlertTitle>
                        <AlertDescription fontSize="xs">Please update the base URL for this client before adding a URL monitor.</AlertDescription>
                      </Box>
                    </Alert>
                  ) : (
                    <Box px={3} py={2} bg={addonBg} borderRadius="md" borderWidth="1px" borderColor={borderColor} fontSize="sm" color={addonColor} fontFamily="mono" userSelect="all">{baseUrl}</Box>
                  )}
                </FormControl>
              )}

              {/* Endpoint */}
              <FormControl isRequired isInvalid={!!endpointError}>
                <FormLabel color={headingColor} fontSize="sm" fontWeight="medium">Endpoint / Path</FormLabel>
                <InputGroup>
                  {baseUrl && !baseUrlMissing && (
                    <InputLeftAddon bg={addonBg} color={addonColor} borderColor={borderColor} fontSize="xs" fontFamily="mono" maxW="220px" isTruncated title={baseUrl}>{baseUrl}</InputLeftAddon>
                  )}
                  <Input
                    placeholder="/health"
                    value={newUrl.endpoint}
                    onChange={e => { setNewUrl({ ...newUrl, endpoint: e.target.value }); setEndpointError(''); }}
                    isDisabled={baseUrlMissing || !newUrl.clientId}
                    bg={inputBg} borderColor={borderColor} color={headingColor} fontFamily="mono"
                    _placeholder={{ color: mutedTextColor }}
                    _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)' }}
                  />
                </InputGroup>
                {endpointError && <Text fontSize="xs" color="red.500" mt={1}>{endpointError}</Text>}
                {baseUrl && newUrl.endpoint && !baseUrlMissing && (
                  <Box mt={2} px={3} py={2} bg={addonBg} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                    <Text fontSize="xs" color={mutedTextColor} mb={0.5} fontWeight="medium">Full URL Preview</Text>
                    <Text fontSize="xs" color="blue.500" fontFamily="mono" wordBreak="break-all">{baseUrl}{newUrl.endpoint}</Text>
                  </Box>
                )}
                <Text fontSize="xs" color={mutedTextColor} mt={1}>e.g. <Text as="span" fontFamily="mono">/health</Text> or <Text as="span" fontFamily="mono">/api/access</Text></Text>
              </FormControl>

              =

              {/* Status */}
              <FormControl>
                <FormLabel color={headingColor} fontSize="sm" fontWeight="medium">Initial Status</FormLabel>
                <HStack spacing={3}>
                  <Switch size="md" colorScheme="green" isChecked={newUrl.status === 1} onChange={e => setNewUrl({ ...newUrl, status: e.target.checked ? 1 : 0 })} />
                  <Text fontSize="sm" color={headingColor} fontWeight="medium">{newUrl.status === 1 ? 'UP (Active)' : 'DOWN (Inactive)'}</Text>
                </HStack>
                <Text fontSize="xs" color={mutedTextColor} mt={1}>Set the initial monitoring status</Text>
              </FormControl>

            </VStack>
          </ModalBody>
          <ModalFooter borderTop="1px" borderTopColor={borderColor}>
            <Button colorScheme="blue" mr={3} onClick={handleSave} isDisabled={baseUrlMissing} _hover={{ bg: blueHover }}>
              {isEditMode ? 'Update Monitor' : 'Add Monitor'}
            </Button>
            <Button variant="ghost" onClick={handleModalClose} color={textColor} _hover={{ bg: hoverBg, color: headingColor }}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Delete Modal ── */}
      <Modal isOpen={deleteModalOpen} onClose={cancelDelete} size="md" isCentered>
        <ModalOverlay bg={modalOverlay} />
        <ModalContent bg={cardBg} borderColor={borderColor} borderWidth="1px">
          <ModalHeader color={headingColor} borderBottom="1px" borderBottomColor={borderColor}>
            <HStack spacing={3}><Box color="red.500"><MdDelete size={24} /></Box><Text>Confirm Deletion</Text></HStack>
          </ModalHeader>
          <ModalCloseButton color={textColor} />
          <ModalBody py={6}>
            <VStack spacing={4} align="stretch">
              <Alert status="warning" variant="left-accent" borderRadius="md">
                <AlertIcon /><Box><AlertTitle fontSize="sm">Warning</AlertTitle><AlertDescription fontSize="sm">This action cannot be undone.</AlertDescription></Box>
              </Alert>
              <Text color={textColor} fontSize="sm">Are you sure you want to delete the following URL monitor?</Text>
              <Card bg={hoverBg} borderWidth="1px" borderColor={borderColor}>
                <CardBody p={3}>
                  <VStack align="stretch" spacing={2}>
                    <HStack justify="space-between"><Text fontSize="xs" color={mutedTextColor}>Client:</Text><Text fontSize="sm" color={headingColor} fontWeight="medium">{urlToDelete?.clientName}</Text></HStack>
                    <HStack justify="space-between"><Text fontSize="xs" color={mutedTextColor}>Full URL:</Text><Text fontSize="sm" color={headingColor} fontWeight="medium" maxW="300px" isTruncated>{urlToDelete?.fullurl}</Text></HStack>
                    <HStack justify="space-between"><Text fontSize="xs" color={mutedTextColor}>ID:</Text><Text fontSize="sm" color={textColor}>{urlToDelete?.id}</Text></HStack>
                  </VStack>
                </CardBody>
              </Card>
            </VStack>
          </ModalBody>
          <ModalFooter borderTop="1px" borderTopColor={borderColor}>
            <Button colorScheme="red" mr={3} onClick={confirmDelete} leftIcon={<MdDelete />}>Delete Monitor</Button>
            <Button variant="ghost" onClick={cancelDelete} color={textColor} _hover={{ bg: hoverBg, color: headingColor }}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </Box>
  );
};

export default UrlManager;